import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CalendarDays, Copy, Mail, Phone, Users, Wallet } from "lucide-react";
import { AiWriteButton } from "./AiWriteButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("en-IE", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

const enquiryContext = (q) =>
  [
    `Customer name: ${q.name}`,
    `Email: ${q.email}`,
    q.phone && `Phone: ${q.phone}`,
    `Occasion / product: ${q.event_type}`,
    q.event_date && `Event date: ${q.event_date}`,
    q.guest_count && `Guests: ${q.guest_count}`,
    q.budget_tier && `Budget: ${q.budget_tier}`,
    q.message && `Message: ${q.message}`,
    q.reference_images?.length &&
      `Customer attached ${q.reference_images.length} reference photo(s).`,
  ]
    .filter(Boolean)
    .join("\n");

const ReplyDraft = ({ q, headers }) => {
  const [draft, setDraft] = useState("");
  const copy = async () => {
    await navigator.clipboard.writeText(draft);
    toast.success("Reply copied");
  };
  return (
    <div className="mt-4 border-t border-[#D4AF37]/15 pt-4">
      <div className="flex flex-wrap items-center gap-2">
        <AiWriteButton
          task="reply"
          label="Draft reply with AI"
          testId={`admin-enquiry-draft-${q.id}`}
          headers={headers}
          context={enquiryContext(q)}
          onResult={setDraft}
        />
        {draft && (
          <>
            <button
              type="button"
              data-testid={`admin-enquiry-copy-${q.id}`}
              onClick={copy}
              className="inline-flex items-center gap-1.5 rounded-full border border-[#1B3A6B]/20 px-3 py-1 text-[11px] font-medium text-[#1B3A6B] transition-colors hover:bg-[#1B3A6B] hover:text-white"
            >
              <Copy size={12} /> Copy
            </button>
            <a
              data-testid={`admin-enquiry-mailto-${q.id}`}
              href={`mailto:${q.email}?subject=${encodeURIComponent(`Your ${q.event_type} enquiry — Tarah Cake Design Studio`)}&body=${encodeURIComponent(draft)}`}
              className="inline-flex items-center gap-1.5 rounded-full bg-[#D4AF37] px-3 py-1 text-[11px] font-semibold text-[#1B3A6B] transition-colors hover:bg-[#8A6D1F] hover:text-white"
            >
              <Mail size={12} /> Open in email
            </a>
          </>
        )}
      </div>
      {draft && (
        <textarea
          data-testid={`admin-enquiry-reply-${q.id}`}
          rows={7}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          className="mt-3 w-full rounded-sm border border-[#D4AF37]/30 bg-[#FDFBF7] px-4 py-3 text-sm leading-relaxed text-[#1B3A6B] focus:border-[#D4AF37] focus:outline-none"
        />
      )}
    </div>
  );
};

export const AdminEnquiries = ({ headers }) => {
  const [enquiries, setEnquiries] = useState([]);

  useEffect(() => {
    axios
      .get(`${API}/admin/enquiries`, { headers })
      .then((r) => setEnquiries(r.data))
      .catch(() => toast.error("Could not load enquiries"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <section className="mt-16" data-testid="admin-enquiries-section">
      <h2 className="font-display text-2xl text-[#1B3A6B]">
        Customer Enquiries
      </h2>
      <p className="mt-2 text-sm text-[#67718A]">
        Every enquiry sent through the website, newest first, with any reference
        images the customer attached.
      </p>
      {enquiries.length === 0 ? (
        <p
          className="mt-6 text-sm italic text-[#67718A]"
          data-testid="admin-enquiries-empty"
        >
          No enquiries yet.
        </p>
      ) : (
        <div className="mt-6 space-y-4">
          {enquiries.map((q) => (
            <div
              key={q.id}
              data-testid={`admin-enquiry-${q.id}`}
              className="rounded-sm border border-[#D4AF37]/25 bg-white p-5"
            >
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <p className="font-display text-lg text-[#1B3A6B]">
                  {q.name}{" "}
                  <span className="ml-2 text-xs uppercase tracking-[0.16em] text-[#9A7B24]">
                    {q.event_type}
                  </span>
                </p>
                <p className="text-xs text-[#67718A]">
                  Received {formatDate(q.created_at)}
                </p>
              </div>
              <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1.5 text-xs text-[#2F4A78]">
                <a
                  href={`mailto:${q.email}`}
                  className="flex items-center gap-1.5 hover:text-[#8A6D1F]"
                >
                  <Mail size={12} className="text-[#D4AF37]" /> {q.email}
                </a>
                {q.phone && (
                  <a
                    href={`tel:${q.phone}`}
                    className="flex items-center gap-1.5 hover:text-[#8A6D1F]"
                  >
                    <Phone size={12} className="text-[#D4AF37]" /> {q.phone}
                  </a>
                )}
                {q.event_date && (
                  <span className="flex items-center gap-1.5">
                    <CalendarDays size={12} className="text-[#D4AF37]" />{" "}
                    {q.event_date}
                  </span>
                )}
                {q.guest_count && (
                  <span className="flex items-center gap-1.5">
                    <Users size={12} className="text-[#D4AF37]" />{" "}
                    {q.guest_count} guests
                  </span>
                )}
                {q.budget_tier && (
                  <span className="flex items-center gap-1.5">
                    <Wallet size={12} className="text-[#D4AF37]" />{" "}
                    {q.budget_tier}
                  </span>
                )}
              </div>
              {q.message && (
                <p className="mt-3 text-sm leading-relaxed text-[#2F4A78]">
                  {q.message}
                </p>
              )}
              {q.reference_images?.length > 0 && (
                <div
                  className="mt-3 flex flex-wrap gap-2"
                  data-testid={`admin-enquiry-images-${q.id}`}
                >
                  {q.reference_images.map((url, i) => (
                    <a
                      key={url}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <img
                        src={url}
                        alt={`Reference ${i + 1} from ${q.name}`}
                        className="h-20 w-24 rounded-sm object-cover"
                      />
                    </a>
                  ))}
                </div>
              )}
              <ReplyDraft q={q} headers={headers} />
            </div>
          ))}
        </div>
      )}
    </section>
  );
};
